import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';
import type { ApiResponse, Cat, Product, CatFilters, ProductFilters, User, AuthResponse, AdoptionRequest, PurchaseRequest, AdoptionStatus, PurchaseStatus } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: false,
});

api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('auth_token');
    if (token && typeof token === 'string' && token !== 'undefined' && token !== 'null' && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (config.data instanceof FormData && config.headers) {
      delete config.headers['Content-Type'];
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiResponse<unknown>>) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export const authApi = {
  register: (email: string, password: string) =>
    api.post<ApiResponse<AuthResponse>>('/auth/register', { email, password }),

  login: (email: string, password: string) =>
    api.post<ApiResponse<AuthResponse>>('/auth/login', { email, password }),
};

export const catsApi = {
  getAll: (filters?: CatFilters) =>
    api.get<ApiResponse<{ cats: Cat[]; pagination: import('../types').PaginationMeta }>>('/cats', { params: filters }),

  getById: (id: string) =>
    api.get<ApiResponse<Cat>>(`/cats/${id}`),

  create: (formData: FormData) =>
    api.post<ApiResponse<Cat>>('/cats', formData),

  update: (id: string, formData: FormData) =>
    api.patch<ApiResponse<Cat>>(`/cats/${id}`, formData),

  delete: (id: string) =>
    api.delete<ApiResponse<null>>(`/cats/${id}`),
};

export const productsApi = {
  getAll: (filters?: ProductFilters) =>
    api.get<ApiResponse<{ products: Product[]; pagination: import('../types').PaginationMeta }>>('/products', { params: filters }),

  getById: (id: string) =>
    api.get<ApiResponse<Product>>(`/products/${id}`),

  create: (formData: FormData) =>
    api.post<ApiResponse<Product>>('/products', formData),

  update: (id: string, formData: FormData) =>
    api.patch<ApiResponse<Product>>(`/products/${id}`, formData),

  delete: (id: string) =>
    api.delete<ApiResponse<null>>(`/products/${id}`),
};

export const adoptionApi = {
  create: (data: { catId: string; phoneNumber: string; message: string; adoptedBefore: boolean }) =>
    api.post<ApiResponse<AdoptionRequest>>('/adoption-requests', data),

  getAll: (status?: AdoptionStatus) =>
    api.get<ApiResponse<AdoptionRequest[]>>('/adoption-requests', { params: status ? { status } : {} }),

  getById: (id: string) =>
    api.get<ApiResponse<AdoptionRequest>>(`/adoption-requests/${id}`),

  updateStatus: (id: string, status: AdoptionStatus) =>
    api.patch<ApiResponse<AdoptionRequest>>(`/adoption-requests/${id}/`, { status }),

  delete: (id: string) =>
    api.delete<ApiResponse<null>>(`/adoption-requests/${id}`),
};

export const purchaseApi = {
  create: (data: { products?: Array<{ productId: string; quantity: number }>; cats?: Array<{ catId: string; quantity: number }>; phoneNumber: string }) =>
    api.post<ApiResponse<PurchaseRequest>>('/purchase-requests', data),

  getAll: (phoneNumber?: string) =>
    api.get<ApiResponse<PurchaseRequest[]>>('/purchase-requests', { params: phoneNumber ? { phoneNumber } : {} }),

  getById: (id: string) =>
    api.get<ApiResponse<PurchaseRequest>>(`/purchase-requests/${id}`),

  update: (id: string, data: { products?: Array<{ productId: string; quantity: number }>; cats?: Array<{ catId: string; quantity: number }>; phoneNumber?: string }) =>
    api.put<ApiResponse<PurchaseRequest>>(`/purchase-requests/${id}`, data),

  confirm: (id: string, soldProducts?: Array<{ productId: string; quantityPurchased: number }>, soldCats?: Array<{ catId: string; quantityPurchased: number }>) =>
    api.put<ApiResponse<PurchaseRequest>>(`/purchase-requests/${id}/confirm`, { soldProducts: soldProducts || [], soldCats: soldCats || [] }),

  reject: (id: string) =>
    api.put<ApiResponse<PurchaseRequest>>(`/purchase-requests/${id}/reject`),

  delete: (id: string) =>
    api.delete<ApiResponse<null>>(`/purchase-requests/${id}`),
};

export const adminApi = {
  getStats: () =>
    api.get<ApiResponse<{
      totalCats: number;
      availableCats: number;
      pendingCats: number;
      adoptedCats: number;
      soldCats: number;
      totalProducts: number;
      lowStockProducts: number;
      outOfStockProducts: number;
      pendingPurchaseRequests: number;
      rejectedPurchaseRequests: number;
      totalPurchases: number;
      pendingAdoptionRequests: number;
      approvedAdoptionRequests: number;
      totalAdoptions: number;
      totalRevenue: number;
    }>>('/admin/stats'),
};

export const notificationsApi = {
  register: (data: { token: string; platform?: string }) =>
    api.post<ApiResponse<{ message: string; tokenId: string }>>('/notifications/register', {
      token: data.token,
      role: 'admin',
      platform: data.platform || 'android',
    }),
};

export default api;